import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { Font } from 'three/examples/jsm/loaders/FontLoader.js';

export interface ParticleTextProps {
  text?: string;
  fontSize?: string;
  transparent?: boolean;
  className?: string;
  style?: React.CSSProperties;
  // Optional particle customization
  particleAmount?: number;
  particleSize?: number;
  particleColor?: number;
  textSize?: number;
  area?: number;
  ease?: number;
  fontUrl?: string;
  particleImageUrl?: string;
}

// Global cache for shared resources
const resourceCache = new Map<string, { font?: Font; texture?: THREE.Texture; loading?: Promise<{ font: Font; texture: THREE.Texture }> }>();

const ParticleText: React.FC<ParticleTextProps> = ({
  text = 'STACKLOOP',
  fontSize = '16px',
  transparent = false,
  className = '',
  style = {},
  particleAmount = 1500,
  particleSize = 1,
  particleColor = 0xffffff,
  textSize,
  area = 250,
  ease = 0.05,
  fontUrl = 'https://res.cloudinary.com/dydre7amr/raw/upload/v1612950355/font_zsd4dr.json',
  particleImageUrl = 'https://res.cloudinary.com/dfvtkoboz/image/upload/v1605013866/particle_a64uzf.png'
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const environmentRef = useRef<Environment | null>(null);

  // Parse fontSize to calculate dimensions like an HTML element
  const parseFontSize = (size: string): number => {
    const numericValue = parseFloat(size);
    if (size.includes('rem')) {
      return numericValue * 16; // Assuming 1rem = 16px
    } else if (size.includes('%')) {
      return (numericValue / 100) * 16; // Assuming base of 16px
    } else {
      return numericValue; // Assume px
    }
  };

  const fontSizeInPx = parseFontSize(fontSize);
  
  // Check if user has provided custom width through style prop
  const hasCustomWidthInStyle = style?.width !== undefined;
  
  // State to track if width is defined via CSS classes (like Tailwind)
  const [hasCustomWidthInCSS, setHasCustomWidthInCSS] = React.useState(false);
  
  // Check for CSS-defined width after component mounts
  React.useEffect(() => {
    if (containerRef.current && !hasCustomWidthInStyle) {
      const computedStyle = window.getComputedStyle(containerRef.current);
      const computedWidth = computedStyle.width;
      // If width is not 'auto' and not '0px', it means it's defined via CSS
      setHasCustomWidthInCSS(computedWidth !== 'auto' && computedWidth !== '0px');
    }
  }, [className, hasCustomWidthInStyle]);
  
  const hasCustomWidth = hasCustomWidthInStyle || hasCustomWidthInCSS;
  
  // Calculate height and width based on fontSize like an h1 element
  const height = Math.min(fontSizeInPx * 1.2, window.innerHeight * 0.8); // Limit height to 80% of viewport
  const width = hasCustomWidth ? undefined : Math.min(text.length * fontSizeInPx * 0.6, window.innerWidth * 0.9); // Use custom width if provided, otherwise calculate
  
  // Convert fontSize to Three.js text size (scale down for particle system)
  const calculatedTextSize = textSize || fontSizeInPx * 0.5;

  useEffect(() => {
    if (!containerRef.current) return;

    const cacheKey = `${fontUrl}|${particleImageUrl}`;
    let isMounted = true;

    const loadResources = async () => {
      try {
        // Check if resources are already cached
         const cached = resourceCache.get(cacheKey);
        
        if (cached?.font && cached?.texture) {
          // Resources are ready
          if (isMounted && containerRef.current) {
            const environment = new Environment(
              containerRef.current,
              cached.font,
              cached.texture,
              {
                text,
                amount: particleAmount,
                particleSize,
                particleColor,
                textSize: calculatedTextSize,
                area,
                ease
              }
            );
            environmentRef.current = environment;
          }
          return;
        }

        // Check if loading is in progress
        if (cached?.loading) {
          const resources = await cached.loading;
          if (isMounted && containerRef.current) {
            const environment = new Environment(
              containerRef.current,
              resources.font,
              resources.texture,
              {
                text,
                amount: particleAmount,
                particleSize,
                particleColor,
                textSize: calculatedTextSize,
                area,
                ease
              }
            );
            environmentRef.current = environment;
          }
          return;
        }

        // Start loading resources
        const loadingPromise = new Promise<{ font: Font; texture: THREE.Texture }>((resolve, reject) => {
          const manager = new THREE.LoadingManager();
          let font: Font | null = null;
          let texture: THREE.Texture | null = null;
          let loadedCount = 0;

          const checkComplete = () => {
            loadedCount++;
            if (loadedCount === 2 && font && texture) {
              // Cache the resources
              resourceCache.set(cacheKey, { font, texture });
              resolve({ font, texture });
            }
          };

          manager.onError = (url) => {
            console.error(`Failed to load resource: ${url}`);
            reject(new Error(`Failed to load resource: ${url}`));
          };

          const fontLoader = new FontLoader();
          fontLoader.load(
            fontUrl,
            (loadedFont) => {
              font = loadedFont;
              checkComplete();
            },
            undefined,
            (error) => {
              console.error('Font loading error:', error);
              reject(error);
            }
          );

          const textureLoader = new THREE.TextureLoader();
          textureLoader.load(
            particleImageUrl,
            (loadedTexture) => {
              texture = loadedTexture;
              checkComplete();
            },
            undefined,
            (error) => {
              console.error('Texture loading error:', error);
              reject(error);
            }
          );
        });

        // Cache the loading promise
        resourceCache.set(cacheKey, { loading: loadingPromise });

        const resources = await loadingPromise;
        
        if (isMounted && containerRef.current) {
          const environment = new Environment(
            containerRef.current,
            resources.font,
            resources.texture,
            {
              text,
              amount: particleAmount,
              particleSize,
              particleColor,
              textSize: calculatedTextSize,
              area,
              ease
            }
          );
          environmentRef.current = environment;
        }
      } catch (error) {
        console.error('ParticleText: Failed to load resources:', error);
        // Remove failed loading promise from cache
        resourceCache.delete(cacheKey);
      }
    };

    loadResources();

    return () => {
      isMounted = false;
      if (environmentRef.current) {
        environmentRef.current.dispose();
      }
    };
  }, [text, fontSize, particleAmount, particleSize, particleColor, calculatedTextSize, area, ease, fontUrl, particleImageUrl]);

  const defaultStyle: React.CSSProperties = {
    position: 'relative',
    ...(width !== undefined && { width: `${width}px` }), // Only set width if calculated (not user-provided)
    height: `${height}px`,
    display: hasCustomWidth ? 'flex' : 'inline-block', // Use flex for centering when custom width is provided
    alignItems: 'center', // Center vertically
    justifyContent: 'center', // Center horizontally
    margin: 0,
    padding: 0,
    backgroundColor: transparent ? 'transparent' : undefined,
    overflow: 'hidden',
    ...style
  };

  return (
    <div 
      ref={containerRef} 
      className={`particle-text-container ${className}`}
      style={defaultStyle}
    />
  );
};

interface ParticleData {
  text: string;
  amount: number;
  particleSize: number;
  particleColor: number;
  textSize: number;
  area: number;
  ease: number;
}

class Environment {
  private font: Font;
  private particle: THREE.Texture;
  private container: HTMLElement;
  private scene: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private createParticles!: CreateParticles;
  private data: ParticleData;

  constructor(container: HTMLElement, font: Font, particle: THREE.Texture, data: ParticleData) {
    this.font = font;
    this.particle = particle;
    this.container = container;
    this.data = data;
    this.scene = new THREE.Scene();
    this.createCamera();
    this.createRenderer();
    this.setup();
    this.bindEvents();
  }

  private bindEvents(): void {
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  private setup(): void {
    this.createParticles = new CreateParticles(
      this.scene,
      this.font,
      this.particle,
      this.camera,
      this.renderer,
      this.container,
      this.data
    );
  }

  private render(): void {
    this.createParticles.render();
    this.renderer.render(this.scene, this.camera);
  }

  private createCamera(): void {
    this.camera = new THREE.PerspectiveCamera(
      65,
      this.container.clientWidth / this.container.clientHeight,
      1,
      10000
    );
    this.camera.position.set(0, 0, 100);
  }

  private createRenderer(): void {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setClearColor(0x000000, 0); // Transparent background
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.container.appendChild(this.renderer.domElement);
    this.renderer.setAnimationLoop(() => {
      this.render();
    });
  }

  private onWindowResize(): void {
    this.camera.aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
  }

  public dispose(): void {
    this.renderer.setAnimationLoop(null);
    this.renderer.dispose();
    if (this.container && this.renderer.domElement) {
      this.container.removeChild(this.renderer.domElement);
    }
    window.removeEventListener('resize', this.onWindowResize.bind(this));
  }
}

class CreateParticles {
  private scene: THREE.Scene;
  private font: Font;
  private particleImg: THREE.Texture;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private container: HTMLElement;
  private raycaster: THREE.Raycaster;
  private mouse: THREE.Vector2;
  private colorChange: THREE.Color;
  private button: boolean;
  private data: ParticleData;
  private planeArea!: THREE.Mesh;
  private particles!: THREE.Points;
  private geometryCopy!: THREE.BufferGeometry;
  private currentPosition: THREE.Vector3;

  constructor(
    scene: THREE.Scene,
    font: Font,
    particleImg: THREE.Texture,
    camera: THREE.PerspectiveCamera,
    renderer: THREE.WebGLRenderer,
    container: HTMLElement,
    data: ParticleData
  ) {
    this.scene = scene;
    this.font = font;
    this.particleImg = particleImg;
    this.camera = camera;
    this.renderer = renderer;
    this.container = container;
    this.data = data;

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2(-200, 200);
    this.colorChange = new THREE.Color();
    this.button = false;
    this.currentPosition = new THREE.Vector3();

    this.setup();
    this.bindEvents();
  }

  private setup(): void {
    const geometry = new THREE.PlaneGeometry(
      this.visibleWidthAtZDepth(100, this.camera),
      this.visibleHeightAtZDepth(100, this.camera)
    );
    const material = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0
    });
    this.planeArea = new THREE.Mesh(geometry, material);
    this.planeArea.visible = false;
    this.createText();
  }

  private bindEvents(): void {
    document.addEventListener('mousedown', this.onMouseDown.bind(this));
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));
  }

  private onMouseDown(event: MouseEvent): void {
    const rect = this.container.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const vector = new THREE.Vector3(this.mouse.x, this.mouse.y, 0.5);
    vector.unproject(this.camera);
    const dir = vector.sub(this.camera.position).normalize();
    const distance = -this.camera.position.z / dir.z;
    this.currentPosition = this.camera.position.clone().add(dir.multiplyScalar(distance));

    this.button = true;
    this.data.ease = 0.01;
  }

  private onMouseUp(): void {
    this.button = false;
    this.data.ease = 0.05;
  }

  private onMouseMove(event: MouseEvent): void {
    const rect = this.container.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  public render(): void {
    const time = ((0.001 * performance.now()) % 12) / 12;
    const zigzagTime = (1 + Math.sin(time * 2 * Math.PI)) / 6;

    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObject(this.planeArea);

    if (intersects.length > 0) {
      const pos = this.particles.geometry.attributes.position as THREE.BufferAttribute;
      const copy = this.geometryCopy.attributes.position as THREE.BufferAttribute;
      const colors = this.particles.geometry.attributes.customColor as THREE.BufferAttribute;
      const size = this.particles.geometry.attributes.size as THREE.BufferAttribute;

      const mx = intersects[0].point.x;
      const my = intersects[0].point.y;

      for (let i = 0, l = pos.count; i < l; i++) {
        const initX = copy.getX(i);
        const initY = copy.getY(i);
        const initZ = copy.getZ(i);

        let px = pos.getX(i);
        let py = pos.getY(i);
        let pz = pos.getZ(i);

        this.colorChange.setHSL(0.5, 1, 1);
        colors.setXYZ(i, this.colorChange.r, this.colorChange.g, this.colorChange.b);
        colors.needsUpdate = true;

        size.array[i] = this.data.particleSize;
        size.needsUpdate = true;

        let dx = mx - px;
        let dy = my - py;

        const mouseDistance = this.distance(mx, my, px, py);
        const d = (dx = mx - px) * dx + (dy = my - py) * dy;
        const f = -this.data.area / d;

        if (this.button) {
          const t = Math.atan2(dy, dx);
          px -= f * Math.cos(t);
          py -= f * Math.sin(t);

          this.colorChange.setHSL(0.5 + zigzagTime, 1.0, 0.5);
          colors.setXYZ(i, this.colorChange.r, this.colorChange.g, this.colorChange.b);
          colors.needsUpdate = true;

          if (
            px > initX + 70 ||
            px < initX - 70 ||
            py > initY + 70 ||
            py < initY - 70
          ) {
            this.colorChange.setHSL(0.15, 1.0, 0.5);
            colors.setXYZ(i, this.colorChange.r, this.colorChange.g, this.colorChange.b);
            colors.needsUpdate = true;
          }
        } else {
          if (mouseDistance < this.data.area) {
            if (i % 5 === 0) {
              const t = Math.atan2(dy, dx);
              px -= 0.03 * Math.cos(t);
              py -= 0.03 * Math.sin(t);

              this.colorChange.setHSL(0.15, 1.0, 0.5);
              colors.setXYZ(i, this.colorChange.r, this.colorChange.g, this.colorChange.b);
              colors.needsUpdate = true;

              size.array[i] = this.data.particleSize / 1.2;
              size.needsUpdate = true;
            } else {
              const t = Math.atan2(dy, dx);
              px += f * Math.cos(t);
              py += f * Math.sin(t);

              pos.setXYZ(i, px, py, pz);
              pos.needsUpdate = true;

              size.array[i] = this.data.particleSize * 1.3;
              size.needsUpdate = true;
            }

            if (
              px > initX + 10 ||
              px < initX - 10 ||
              py > initY + 10 ||
              py < initY - 10
            ) {
              this.colorChange.setHSL(0.15, 1.0, 0.5);
              colors.setXYZ(i, this.colorChange.r, this.colorChange.g, this.colorChange.b);
              colors.needsUpdate = true;

              size.array[i] = this.data.particleSize / 1.8;
              size.needsUpdate = true;
            }
          }
        }

        px += (initX - px) * this.data.ease;
        py += (initY - py) * this.data.ease;
        pz += (initZ - pz) * this.data.ease;

        pos.setXYZ(i, px, py, pz);
        pos.needsUpdate = true;
      }
    }
  }

  private createText(): void {
    const thePoints: THREE.Vector3[] = [];
    const shapes = this.font.generateShapes(this.data.text, this.data.textSize);
    const geometry = new THREE.ShapeGeometry(shapes);
    geometry.computeBoundingBox();

    const xMid = -0.5 * (geometry.boundingBox!.max.x - geometry.boundingBox!.min.x);
    const yMid = -0.5 * (geometry.boundingBox!.max.y - geometry.boundingBox!.min.y);

    geometry.center();

    const holeShapes: THREE.Path[] = [];

    for (let q = 0; q < shapes.length; q++) {
      const shape = shapes[q];
      if (shape.holes && shape.holes.length > 0) {
        for (let j = 0; j < shape.holes.length; j++) {
          const hole = shape.holes[j];
          // Convert Path to Shape-like object for processing
          const holeShape = {
            ...hole,
            type: 'Path' as const,
            getSpacedPoints: hole.getSpacedPoints.bind(hole)
          };
          holeShapes.push(holeShape as THREE.Path);
        }
      }
    }
    shapes.push(...(holeShapes as THREE.Shape[]));

    const colors: number[] = [];
    const sizes: number[] = [];

    for (let x = 0; x < shapes.length; x++) {
      const shape = shapes[x];
      const amountPoints = shape.type === 'Path' ? this.data.amount / 2 : this.data.amount;
      const points = shape.getSpacedPoints(amountPoints);

      points.forEach((element: THREE.Vector2) => {
        const a = new THREE.Vector3(element.x, element.y, 0);
        thePoints.push(a);
        colors.push(this.colorChange.r, this.colorChange.g, this.colorChange.b);
        sizes.push(1);
      });
    }

    const geoParticles = new THREE.BufferGeometry().setFromPoints(thePoints);
    geoParticles.translate(xMid, yMid, 0);
    geoParticles.setAttribute('customColor', new THREE.Float32BufferAttribute(colors, 3));
    geoParticles.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1));

    // Create shader material with embedded shaders
    const vertexShader = `
      attribute float size;
      attribute vec3 customColor;
      varying vec3 vColor;
      void main() {
        vColor = customColor;
        vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
        gl_PointSize = size * ( 300.0 / -mvPosition.z );
        gl_Position = projectionMatrix * mvPosition;
      }
    `;

    const fragmentShader = `
      uniform vec3 color;
      uniform sampler2D pointTexture;
      varying vec3 vColor;
      void main() {
        gl_FragColor = vec4( color * vColor, 1.0 );
        gl_FragColor = gl_FragColor * texture2D( pointTexture, gl_PointCoord );
      }
    `;

    const material = new THREE.ShaderMaterial({
      uniforms: {
        color: { value: new THREE.Color(0xffffff) },
        pointTexture: { value: this.particleImg }
      },
      vertexShader,
      fragmentShader,
      blending: THREE.AdditiveBlending,
      depthTest: false,
      transparent: true
    });

    this.particles = new THREE.Points(geoParticles, material);
    this.scene.add(this.particles);

    this.geometryCopy = new THREE.BufferGeometry();
    this.geometryCopy.copy(this.particles.geometry);
  }

  private visibleHeightAtZDepth(depth: number, camera: THREE.PerspectiveCamera): number {
    const cameraOffset = camera.position.z;
    if (depth < cameraOffset) depth -= cameraOffset;
    else depth += cameraOffset;

    const vFOV = (camera.fov * Math.PI) / 180;
    return 2 * Math.tan(vFOV / 2) * Math.abs(depth);
  }

  private visibleWidthAtZDepth(depth: number, camera: THREE.PerspectiveCamera): number {
    const height = this.visibleHeightAtZDepth(depth, camera);
    return height * camera.aspect;
  }

  private distance(x1: number, y1: number, x2: number, y2: number): number {
    return Math.sqrt(Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2));
  }
}

export default ParticleText;